import React from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { isAdminUser } from '../lib/roleGates';
import { insightsPathFor } from '../pages/insights/insightsTabs';

// Every address the web app has ever had keeps working (web app v4 plan §6).
// Emails in inboxes, the Mac, Windows and iPhone apps, skills and server
// headers link to these, so the rows are permanent: add one when a page
// moves, never delete one. Each redirect keeps the query string and hash.

export interface RedirectFacts {
  isAdmin: boolean;
  isEa: boolean;
  params: Readonly<Record<string, string | undefined>>;
  /** The old address's query, for a target that maps it itself. */
  search: string;
}

export interface LegacyRedirect {
  /** A route pattern, as react-router matches it. */
  from: string;
  /** Where it goes now: a path (with an optional query), or one chosen for who's asking. */
  to: string | ((facts: RedirectFacts) => string);
  /** Who still links here. */
  linkedFrom: string;
  /** The target already carries the old query (mapped); don't add it again. */
  ownQuery?: boolean;
}

export const LEGACY_REDIRECTS: readonly LegacyRedirect[] = [
  // Organisation (Phase 1)
  { from: '/admin', to: '/organisation/people', linkedFrom: 'nav' },
  {
    from: '/billing',
    to: ({ isAdmin }) => (isAdmin ? '/organisation/billing' : '/settings/plan'),
    linkedFrom: 'emails, upgrade prompts, LockedFeatureCard, Mac upgrade prompt (PlanUpgradePresenter.swift)',
  },
  { from: '/teams', to: '/organisation/teams', linkedFrom: 'nav, Team notifications' },
  { from: '/audit-log', to: '/organisation/audit-log', linkedFrom: 'nav' },
  { from: '/webhooks', to: '/organisation/ai-integrations', linkedFrom: 'nav' },
  { from: '/prompt-management', to: '/organisation/ai-integrations', linkedFrom: 'nav' },
  // Settings (Phase 1b)
  { from: '/preferences', to: '/settings', linkedFrom: 'nav, emails, Windows' },
  { from: '/setup', to: '/settings', linkedFrom: 'emails, rail, native apps (keeps ?setup= drawers)' },
  { from: '/setup/health', to: '/settings/assistants#setup-health', linkedFrom: 'emails, notifications' },
  { from: '/connections', to: '/settings/connected-apps', linkedFrom: 'Windows, Setup health' },
  { from: '/context', to: '/settings/about-you', linkedFrom: 'nav' },
  {
    // An executive's own profile is in About you; an assistant's are their
    // executives', under Assistant.
    from: '/principal-profile',
    to: ({ isEa }) => (isEa ? '/assistant/profiles' : '/settings/about-you'),
    linkedFrom: 'nav',
  },
  // Navigation (Phase 2): nine places
  { from: '/work-items', to: '/work', linkedFrom: 'nav, emails, notifications, Windows (keeps ?id=, ?action=create, ?projectId=)' },
  { from: '/delegation', to: '/work?filter=delegated', linkedFrom: 'nav' },
  { from: '/ideas', to: '/review/ideas', linkedFrom: 'nav' },
  { from: '/transcripts', to: '/review/captured?type=transcript', linkedFrom: 'nav' },
  {
    from: '/transcripts/:transcriptId',
    to: ({ params }) => `/review/captured?type=transcript&id=${encodeURIComponent(params.transcriptId ?? '')}`,
    linkedFrom: 'Mac transcript inbox (TranscriptInboxSheet.swift)',
  },
  { from: '/images', to: '/review/captured?type=image', linkedFrom: 'nav' },
  { from: '/strategy', to: '/drivers', linkedFrom: 'nav, Mac Settings' },
  { from: '/horizon', to: '/drivers/horizon', linkedFrom: 'nav ("Outlook")' },
  { from: '/capacity', to: '/focus/capacity', linkedFrom: 'nav' },
  { from: '/ea-workspace', to: '/assistant/workspace', linkedFrom: 'nav, EA home' },
  {
    // Old tab numbers (?tab=4) map to Insights' tabs; other query keys stay.
    from: '/reports',
    to: ({ search }) => insightsPathFor(search),
    ownQuery: true,
    linkedFrom: 'nav, the weekly email (before it moved to /insights/summary)',
  },
  { from: '/reflections', to: '/insights/reflections', linkedFrom: 'nav, emails, Mac (RootView.swift)' },
  {
    from: '/principal-profile/:execId',
    to: ({ params }) => `/assistant/profiles/${encodeURIComponent(params.execId ?? '')}`,
    linkedFrom: 'EA Workspace',
  },
  // Older aliases
  { from: '/strategic-drivers', to: '/drivers', linkedFrom: 'Mac Settings' },
  { from: '/focus-time', to: '/focus', linkedFrom: 'old alias' },
  { from: '/dashboard', to: '/', linkedFrom: 'Mac, emails' },
  { from: '/workitems', to: '/work', linkedFrom: 'Mac "Open in Cadence" (TodayView.swift, WorkItemsView.swift)' },
  {
    from: '/workitems/:workItemId',
    to: ({ params }) => `/work?id=${encodeURIComponent(params.workItemId ?? '')}`,
    linkedFrom: 'Mac "Open in Cadence" on an item (WorkItemDetailSheet.swift)',
  },
];

/**
 * Setup's old "Change" links (`/getting-started?edit=N`) open the Settings row
 * for that answer instead of a wizard step. Without `edit` it's first-run setup.
 */
export const SETUP_EDIT_REDIRECTS: Readonly<Record<string, string>> = {
  '2': '/settings/assistants',
  '3': '/settings/your-day',
  '4': '/settings/your-day',
  '5': '/drivers',
};

/** Where `/getting-started` with this query goes instead of the wizard, if anywhere. */
export function setupEditTarget(search: string): string | null {
  return SETUP_EDIT_REDIRECTS[new URLSearchParams(search).get('edit') ?? ''] ?? null;
}

/** The target's own query first, then the old address's (minus any key the target already set). */
export function mergeSearch(targetQuery: string, oldSearch: string): string {
  const merged = new URLSearchParams(targetQuery);
  new URLSearchParams(oldSearch).forEach((value, key) => {
    if (!merged.has(key)) merged.append(key, value);
  });
  const s = merged.toString();
  return s ? `?${s}` : '';
}

function LegacyRedirectElement({ redirect }: { redirect: LegacyRedirect }) {
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  const target = typeof redirect.to === 'string'
    ? redirect.to
    : redirect.to({ isAdmin: isAdminUser(user), isEa: user?.role === 'ExecutiveAssistant', params, search: location.search });
  const [beforeHash, targetHash = ''] = target.split('#');
  const [pathname, targetQuery = ''] = beforeHash.split('?');
  const search = redirect.ownQuery ? (targetQuery ? `?${targetQuery}` : '') : mergeSearch(targetQuery, location.search);
  return <Navigate replace to={{ pathname, search, hash: location.hash || (targetHash ? `#${targetHash}` : '') }} />;
}

/** One <Route> per old address, for inside the signed-in <Routes>. */
export function legacyRedirectRoutes(): React.ReactElement[] {
  return LEGACY_REDIRECTS.map(r => (
    <Route key={r.from} path={r.from} element={<LegacyRedirectElement redirect={r} />} />
  ));
}
